// navigation/command-palette.tsx
// "/" jump-to palette over the nav destinations the user can see. Type-filter,
// arrow/enter/esc, hover syncs the active index. "hidden" features are excluded;
// "locked" ones show a lock and route to the upsell modal instead of navigating.
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, Lock } from "lucide-react";
import { FEATURES, SECTIONS, type FeatureId } from "./nav-config";
import { type FeatureStates, stateOf } from "./use-entitlements";

const SECTION_LABEL: Record<string, string> = Object.fromEntries(
  SECTIONS.map((s) => [s.id, s.label]),
);

export function CommandPalette({
  open,
  states,
  onClose,
  onNavigate,
  onLocked,
}: {
  open: boolean;
  states: FeatureStates;
  onClose: () => void;
  onNavigate: (page: string) => void;
  onLocked: (id: FeatureId) => void;
}) {
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setIdx(0);
      // Render visible first; focus on the next frame.
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return FEATURES.filter(
      (f) =>
        stateOf(states, f.id) !== "hidden" &&
        (!needle ||
          f.label.toLowerCase().includes(needle) ||
          SECTION_LABEL[f.section].toLowerCase().includes(needle)),
    );
  }, [q, states]);

  if (!open) return null;

  const choose = (i: number) => {
    const f = items[i];
    if (!f) return;
    if (stateOf(states, f.id) === "locked") onLocked(f.id);
    else onNavigate(f.page);
    onClose();
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(items.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); choose(idx); }
    else if (e.key === "Escape") { e.preventDefault(); onClose(); }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 flex items-start justify-center"
      style={{ zIndex: 70, background: "rgba(16,18,22,0.40)", paddingTop: "12vh" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Jump to a destination"
        style={{
          width: 520, maxWidth: "92vw", background: "var(--popover)",
          border: "1px solid var(--border)", borderRadius: 14,
          boxShadow: "var(--elevation-lg)", overflow: "hidden",
        }}
      >
        <div
          className="flex items-center text-muted-foreground"
          style={{ gap: 11, padding: "14px 16px", borderBottom: "1px solid var(--border)" }}
        >
          <Search style={{ width: 18, height: 18, strokeWidth: 1.75 }} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setIdx(0); }}
            onKeyDown={onKey}
            placeholder="Jump to a destination…"
            className="flex-1 bg-transparent text-foreground"
            style={{ border: "none", outline: "none", fontSize: 15, fontFamily: "inherit" }}
          />
          <span className="font-mono" style={{ fontSize: 10, border: "1px solid var(--border)", borderRadius: 5, padding: "2px 6px" }}>Esc</span>
        </div>

        <div style={{ maxHeight: "50vh", overflowY: "auto", padding: 6 }}>
          {items.map((f, i) => {
            const Icon = f.icon;
            const locked = stateOf(states, f.id) === "locked";
            return (
              <button
                key={f.id}
                onClick={() => choose(i)}
                onMouseEnter={() => setIdx(i)}
                className="w-full flex items-center text-left"
                style={{
                  gap: 11, padding: "9px 11px", borderRadius: 9, border: "none",
                  cursor: "pointer", fontFamily: "inherit",
                  background: i === idx ? "var(--muted)" : "transparent",
                }}
              >
                <span
                  className="inline-flex items-center justify-center shrink-0 text-muted-foreground"
                  style={{ width: 28, height: 28, borderRadius: 7, background: "var(--muted)" }}
                >
                  <Icon style={{ width: 16, height: 16, strokeWidth: 1.75 }} />
                </span>
                <span className="flex-1 min-w-0 text-foreground" style={{ fontSize: 13.5, fontWeight: 500 }}>{f.label}</span>
                {locked && <Lock className="text-muted-foreground" style={{ width: 13, height: 13, strokeWidth: 2 }} />}
                <span className="text-muted-foreground" style={{ fontSize: 11 }}>{SECTION_LABEL[f.section]}</span>
              </button>
            );
          })}
          {items.length === 0 && (
            <div className="text-muted-foreground" style={{ padding: 24, textAlign: "center", fontSize: 13 }}>No matches.</div>
          )}
        </div>

        <div
          className="flex items-center text-muted-foreground"
          style={{ gap: 14, padding: "9px 16px", borderTop: "1px solid var(--border)", fontSize: 11 }}
        >
          <span className="flex items-center" style={{ gap: 5 }}><span className="font-mono" style={{ border: "1px solid var(--border)", borderRadius: 4, padding: "0 4px" }}>↑↓</span> navigate</span>
          <span className="flex items-center" style={{ gap: 5 }}><span className="font-mono" style={{ border: "1px solid var(--border)", borderRadius: 4, padding: "0 4px" }}>↵</span> open</span>
        </div>
      </div>
    </div>
  );
}
